import { useCallback } from "react";
import { flushSync } from "react-dom";
import { Alert, Button, CircularProgress } from "@mui/material";
import cx from "classnames";
import { useSetAtom } from "jotai";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { comparePitsFormSubmittedAtom } from "../../ComparativeAnalysisForm.atoms";
import type { ComparisonHistoryPayload } from "../../ComparativeAnalysisForm.types";
import { useSetComparisonHistoryAtom } from "../../hooks/useSetComparisonHistoryAtom";

interface ComparativeAnalysisFormSubmitProps {
  onSubmit?: () => ComparisonHistoryPayload;
  hasValidFormFields?: boolean;
  hasIdenticalGroupFields?: boolean;
  allFieldsEmpty?: boolean;
  requiredFieldsEmpty?: boolean;
}

const ComparativeAnalysisFormSubmit = ({
  onSubmit,
  hasValidFormFields,
  hasIdenticalGroupFields,
  allFieldsEmpty,
  requiredFieldsEmpty,
}: ComparativeAnalysisFormSubmitProps) => {
  const { isDebugMode } = useGlobalLocalContext();
  const addHistoryItem = useSetComparisonHistoryAtom();
  const setSubmitted = useSetAtom(comparePitsFormSubmittedAtom);

  const disabled =
    requiredFieldsEmpty || hasIdenticalGroupFields || !hasValidFormFields;

  const onSubmitBtnClick = useCallback(() => {
    // flushSync forces React to commit DOM changes synchronously so the
    // View Transitions API can snapshot the old and new DOM in the same callback.
    document.startViewTransition(() => {
      flushSync(() => {
        if (onSubmit && typeof onSubmit === "function") {
          const historyItem = onSubmit();
          addHistoryItem(historyItem);
        }

        setSubmitted(true);
      });
    });
  }, [setSubmitted, addHistoryItem, onSubmit]);

  const pending = hasValidFormFields === undefined && !allFieldsEmpty;

  return (
    <div className="relative mt-6 w-full">
      <Button
        variant="contained"
        size="large"
        sx={{ width: "100%" }}
        disabled={isDebugMode ? false : disabled}
        onClick={onSubmitBtnClick}
        data-ph-capture-attribute-action="compare_versions"
      >
        {pending && <CircularProgress size={16} sx={{ mr: 2 }} />}
        {pending ? "Validating…" : "Compare Versions"}
      </Button>

      <Alert
        severity="error"
        variant="filled"
        className={cx("absolute inset-0 p-0! place-content-center items-center", {
          "invisible ": !hasIdenticalGroupFields || pending,
        })}
      >
        The two compared versions must not be identical
      </Alert>
    </div>
  );
};

export default ComparativeAnalysisFormSubmit;
